import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

// Locks background page scroll while at least one Modal is open. A plain per-instance
// effect would work too, but SignInPopUp mounts two <Modal>s at once (sign-up + OTP) and
// flips one's `show` off exactly as the other flips on, so a shared counter avoids any
// chance of the unlock from the closing modal racing ahead of the lock from the opening one.
let openModalCount = 0;

const useBodyScrollLock = (locked) => {
  useEffect(() => {
    if (!locked) return undefined;
    // The page's actual scroller is <html> in standards mode, not <body> — locking
    // overflow on body alone leaves wheel/touch scroll working via documentElement.
    if (openModalCount === 0) {
      document.documentElement.style.overflow = "hidden";
    }
    openModalCount += 1;
    return () => {
      openModalCount -= 1;
      if (openModalCount === 0) {
        document.documentElement.style.overflow = "";
      }
    };
  }, [locked]);
};

// Shared Tailwind + Framer Motion modal shell, used by LoginPopup/SignInPopUp
// (previously react-bootstrap's <Modal>) so every dialog in the app animates
// and looks consistent, without pulling in Bootstrap's own modal/transition JS.
const Modal = ({ show, onClose, title, children, footer, maxWidth = "max-w-md", maxHeight = "90vh" }) => {
  useBodyScrollLock(show);

  return (
  <AnimatePresence>
    {show && (
      <motion.div
        className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        <motion.div
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === "string" ? title : undefined}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full ${maxWidth} bg-white rounded-2xl shadow-elevated flex flex-col overflow-hidden`}
          // `vh` is the TALL viewport on phones (measured with the address bar / bottom bar
          // hidden), but this fixed overlay is only as tall as what is actually visible. A
          // bare "99.5vh" therefore made the dialog taller than the screen whenever the
          // browser toolbars were showing, pushing the header X above the top edge and the
          // footer button under the bottom bar (user-reported again 2026-10-04, after the
          // pinned header/footer fix of 2026-09-30 - desktop emulation never shows it).
          // Capping at 100% of the overlay's content box (min with the caller's vh cap)
          // keeps both always on screen, whatever the toolbars are doing.
          style={{ maxHeight: `min(${maxHeight}, 100%)` }}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                // 40px tap target (was a 28px p-1 button, easy to miss with a thumb); the
                // negative margins keep the header the same height as before.
                className="-my-2 -mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {/* The only scrollable region — the header/footer above/below stay pinned on screen
              (as `shrink-0` flex items outside this overflow box) no matter how far the body
              scrolls, so a long form (e.g. CollegeListPage's mobile Filters modal) never leaves
              a user scrolled to a point where both the X and the footer's close/submit button
              are off-screen at once (user-reported 2026-09-30: "filter close goes hidden"). */}
          <div className="px-5 py-4 overflow-y-auto overscroll-contain min-h-0 flex-1">{children}</div>
          {footer ? <div className="px-5 py-4 border-t border-gray-200 shrink-0">{footer}</div> : null}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
  );
};

// Tailwind replacements for the react-bootstrap Form.Control/Form.Select this
// modal's callers used to rely on, so the same isInvalid + feedback pattern works
// without react-bootstrap.
export const FormGroup = ({ label, htmlFor, error, children, className = "" }) => (
  <div className={`mt-3 first:mt-0 ${className}`}>
    {label && (
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
    )}
    {children}
    {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
  </div>
);

const baseFieldClasses =
  "block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500";

export const FormControl = React.forwardRef(function FormControl({ isInvalid, className = "", ...props }, ref) {
  return (
    <input
      ref={ref}
      className={`${baseFieldClasses} ${isInvalid ? "border-red-400" : "border-gray-300"} ${className}`}
      {...props}
    />
  );
});

export const FormSelect = React.forwardRef(function FormSelect({ isInvalid, className = "", children, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={`${baseFieldClasses} bg-white ${isInvalid ? "border-red-400" : "border-gray-300"} ${className}`}
      {...props}
    >
      {children}
    </select>
  );
});

export default Modal;
